import { useEffect, useRef, type CSSProperties } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { hiddenFailures, objectiveState } from '../../lib/grade';
import { ease, prefersReducedMotion } from '../../lib/motion';
import { sound } from '../../lib/sound';
import { useStrings } from '../../state/useLocale';
import type { ObjectivesProps } from '../contracts';

/**
 * The quest log.
 *
 * An objective flipping to passed is the core feedback loop of the whole
 * product, so that moment is the one thing on this screen allowed to be loud.
 * Marks snap to filled in order, 140ms apart, and each one that lands plays a
 * note a step higher than the last, so four objectives passing at once reads
 * and sounds like a run rather than a single event. The snap itself is CSS
 * (motion.css, "Objectives resolve"), timed by `--i` on the mark; the job
 * here is to put the same beat number on the mark and on its note.
 *
 * Which marks are resolving is the parent's call, because the verdict under
 * this list waits for the same count; it arrives as `resolving`, in list
 * order. This component still owns the sound for a check that achieved
 * nothing, since sound is what carries state here, and the guard that keeps a
 * restored session from replaying sounds it did not earn this visit.
 */

const STAGGER_MS = 140;

const NONE: string[] = [];

export function Objectives({ mission, grade, resolving = NONE }: ObjectivesProps) {
  const s = useStrings().play.objectives;
  const seenAt = useRef<number | null>(null);
  const mounted = useRef(false);

  useEffect(() => {
    // A session restored mid-visit arrives with a grade already attached.
    // Replaying its sounds on mount would be a lie about what just happened.
    if (!mounted.current) {
      mounted.current = true;
      seenAt.current = grade?.gradedAt ?? null;
      return;
    }
    if (!grade || grade.gradedAt === seenAt.current) return;
    seenAt.current = grade.gradedAt;

    if (resolving.length === 0) {
      // Nothing flipped. That only means the check achieved nothing if the
      // level is not already solved: re-checking a cleared level must not
      // sound like a mistake.
      if (!grade.passed) sound.fail();
      return;
    }

    // One note per snap, on the beat the CSS delay puts the mark on. Reduced
    // motion collapses the snaps to a single frame, so the notes follow.
    const reduced = prefersReducedMotion();
    const timers = resolving.map((_, i) => window.setTimeout(() => sound.objective(i), reduced ? 0 : i * STAGGER_MS));
    return () => timers.forEach(clearTimeout);
  }, [grade, resolving]);

  const byId = new Map((grade?.objectives ?? []).map((o) => [o.id, o]));

  // A mark's beat is its position in this check's batch, not in the list, so
  // the run starts on the first frame instead of waiting out the slots of
  // rows above it that did not change. Every other mark gets 0. A CSS
  // animation reads its delay only when its name first applies, and a mark
  // that already held its class is not starting one, so the value there is
  // never read.
  const beat = new Map(resolving.map((id, i) => [id, i]));

  const rows = mission.objectives.map((o) => {
    const r = byId.get(o.id);
    return { key: o.id, state: objectiveState(r), title: o.title, msg: !r?.passed ? r?.message : undefined };
  });

  // Hidden checks stay invisible until they fail, so a shallow hack is
  // revealed at the moment it is caught and never signposted in advance.
  for (const r of hiddenFailures(grade)) {
    rows.push({ key: `hidden:${r.id}`, state: 'is-fail', title: s.hiddenCheck(r.title), msg: r.message });
  }

  return (
    <ul className="objectives">
      {rows.map((row) => {
        const hidden = row.key.startsWith('hidden:');
        const style: CSSProperties & { '--i': number } = { '--i': beat.get(row.key) ?? 0 };
        return (
          <li key={row.key} className={`obj ${row.state}${hidden ? ' is-hidden' : ''}`}>
            <span className="obj-mark" aria-hidden="true" style={style} />
            <span className="obj-body">
              <span className="obj-title">{row.title}</span>
              <AnimatePresence initial={false}>
                {row.msg && (
                  <motion.span
                    key={row.msg}
                    className="obj-msg"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={ease}
                  >
                    {row.msg}
                  </motion.span>
                )}
              </AnimatePresence>
            </span>
          </li>
        );
      })}
    </ul>
  );
}

export default Objectives;
